// src/components/DocDynamicFieldsForm.js
import React, { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaCheck } from "react-icons/fa";
import { FiMic } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";

const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const DETAILS_SOFT_WARNING_LENGTH = 15000;
const DETAILS_MAX_LENGTH = 25000;

const toPositiveInteger = (value, fallback = null) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.trunc(parsed);
};

const fieldHardLimit = (field = {}) =>
  toPositiveInteger(
    field.maxLength,
    field.id === "details" ? DETAILS_MAX_LENGTH : null
  );

const fieldSoftLimit = (field = {}, hardLimit = null) => {
  const requested = toPositiveInteger(
    field.softWarningLength,
    field.id === "details" ? DETAILS_SOFT_WARNING_LENGTH : null
  );

  if (!requested) return null;
  if (!hardLimit) return requested;
  return Math.min(requested, hardLimit);
};

const stringValue = (value) =>
  value === null || value === undefined ? "" : String(value);

const DocDynamicFieldsForm = ({
  schema = [],
  values = {},
  onChange,
  onValidityChange,
  uid,       // kept for API parity (unused here)
  email,     // kept for API parity (unused here)
  sessionId, // kept for API parity (unused here)
  userType,  // kept for API parity (unused here)

  // NEW: optionally render only a specific field id or a list of ids.
  // If omitted, component renders all fields (backward compatible).
  onlyFieldIds = null,

  // Voice access is owned by the page hosting this form.
  //
  // "start"  -> new-document flow; the Start Page supplies live billing entitlement.
  // "editor" -> an already-authorized D1/R1/R2 lifecycle; current plan/quota is not
  //             re-checked here. The Editor Page must opt in explicitly.
  voiceAccessContext = "start",
  voiceAccessAllowed = false,
  voiceAccessLoading = false,
  onVoiceBlocked = null,
  voiceSessionKind = "fast_chat",
}) => {
  // ---------- derive effective schema (optionally filtered) ----------
  const effectiveSchema = useMemo(() => {
    if (!onlyFieldIds) return schema;
    const allow = Array.isArray(onlyFieldIds)
      ? new Set(onlyFieldIds)
      : new Set([onlyFieldIds]);
    return schema.filter((f) => allow.has(f.id));
  }, [schema, onlyFieldIds]);

  // ---------- initial state builders ----------
  const buildInitial = () => {
    const base = {};
    for (const f of effectiveSchema) {
      const incoming = values[f.id];
      if (incoming !== undefined) {
        base[f.id] = incoming;
        continue;
      }
      if (f.defaultValue !== undefined) {
        base[f.id] = f.defaultValue;
        continue;
      }
      if (f.type === "checkbox") {
        base[f.id] = false;
      } else if (f.type === "length") {
        // length field stores { mode, value }
        base[f.id] = { mode: "auto", value: null };
      } else {
        base[f.id] = "";
      }
    }
    return base;
  };

  const { currentUser } = useAuth();

  const normalizedVoiceAccessContext = useMemo(
    () =>
      String(voiceAccessContext || "start")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_"),
    [voiceAccessContext]
  );
  const voiceUsesExistingDocumentEntitlement = [
    "editor",
    "editor_page",
    "doc_writer_editor",
    "existing_document",
  ].includes(normalizedVoiceAccessContext);

  const [formValues, setFormValues] = useState(buildInitial);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [limitNotices, setLimitNotices] = useState({});
  const composingFieldsRef = useRef(new Set());

  // ---------------------------------------------------------------
  // Voice input: copied from ChatInput's proven MediaRecorder + STT
  // flow, then adapted so successful transcription appends only to
  // the Key Facts / Details field.
  // ---------------------------------------------------------------
  const [recording, setRecording] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);
  const [voiceText, setVoiceText] = useState("");
  const [waveformData, setWaveformData] = useState([]);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isAcceptingVoice, setIsAcceptingVoice] = useState(false);
  const [voiceNotice, setVoiceNotice] = useState(null);

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const streamRef = useRef(null);
  const recordedBlobRef = useRef(null);
  const recordedMimeRef = useRef("");
  const pendingAcceptRef = useRef(false);
  const stopPromiseRef = useRef(null);
  const recordStartAtRef = useRef(0);
  const isFlatRef = useRef(false);
  const lastVoiceActionAtRef = useRef(0);
  const lastVoiceNoticeRef = useRef({ key: "", at: 0 });
  const detailsTextareaRef = useRef(null);
  const detailsSelectionRef = useRef({ start: null, end: null });
  const pendingDetailsCaretRef = useRef(null);

  const MIN_RECORD_MS = 1000;
  const MIN_BLOB_BYTES = 15000;

  // ChatInput-compatible spinner animation.
  useEffect(() => {
    const STYLE_ID = "asklawly-docwriter-voice-keyframes";
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.type = "text/css";
    style.textContent = `
@keyframes asklawlyDocWriterSpin360 {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;
    document.head.appendChild(style);
  }, []);

  // Same lightweight waveform cadence used by ChatInput.
  useEffect(() => {
    if (isTranscribing || isAcceptingVoice) return;
    if (!recording && !voiceReady) return;

    const interval = window.setInterval(() => {
      const sample = isFlatRef.current ? 2 : Math.random() * 20;
      setWaveformData((data) => [...data.slice(-100), sample]);
    }, 60);

    return () => window.clearInterval(interval);
  }, [recording, voiceReady, isTranscribing, isAcceptingVoice]);

  useEffect(() => {
    if (!voiceNotice) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setVoiceNotice(null);
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [voiceNotice]);

  // Stop microphone hardware and clear recorder state on unmount.
  useEffect(() => {
    return () => {
      try {
        mediaRecorderRef.current?.stop?.();
      } catch {}
      mediaRecorderRef.current = null;

      try {
        streamRef.current?.getTracks?.().forEach((track) => {
          try {
            track.stop();
          } catch {}
        });
      } catch {}
      streamRef.current = null;

      recordedChunksRef.current = [];
      recordedBlobRef.current = null;
      recordedMimeRef.current = "";
      pendingAcceptRef.current = false;
      stopPromiseRef.current = null;
      recordStartAtRef.current = 0;
    };
  }, []);

  // track schema changes without changing UI
  const schemaSig = useMemo(
    () =>
      effectiveSchema
        .map(
          (f) =>
            `${f.id}:${f.type}:${f.required ? 1 : 0}:` +
            `${f.minLength || ""}:${f.maxLength || ""}:` +
            `${f.softWarningLength || ""}`
        )
        .join("|"),
    [effectiveSchema]
  );
  const prevSchemaSigRef = useRef(schemaSig);

  // ---------- reset on schema change ----------
  useEffect(() => {
    if (prevSchemaSigRef.current !== schemaSig) {
      setFormValues(buildInitial());
      setTouched({});
      setErrors({});
      setLimitNotices({});
      prevSchemaSigRef.current = schemaSig;
    }
    // eslint-disable-next-line
  }, [schemaSig]);

  // ---------- validation ----------
  const validateField = (field, value) => {
    const { required, minLength, pattern, type, label, min, max } = field;
    const maxLength = fieldHardLimit(field);

    // Special validation for length picker
    if (type === "length") {
      const v = value || {};
      const mode = v.mode || "auto";
      const num = v.value;

      if (required && mode === "auto" && field.requireNonAuto) {
        return `${label} is required.`;
      }
      if (mode === "pages") {
        if (num === null || num === "" || Number.isNaN(Number(num)))
          return `${label}: please enter the number of pages.`;
        if (Number(num) <= 0) return `${label}: pages must be ≥ 1.`;
        if (field.maxPages && Number(num) > field.maxPages)
          return `${label}: pages must be ≤ ${field.maxPages}.`;
      }
      if (mode === "words") {
        if (num === null || num === "" || Number.isNaN(Number(num)))
          return `${label}: please enter the word count.`;
        if (Number(num) < (field.minWords || 50))
          return `${label}: words must be ≥ ${field.minWords || 50}.`;
        if (field.maxWords && Number(num) > field.maxWords)
          return `${label}: words must be ≤ ${field.maxWords}.`;
      }
      return null;
    }

    // Standard validations
    if (required) {
      if (type === "checkbox") {
        if (!value) return `${label} is required.`;
      } else if (value === "" || value === null || value === undefined) {
        return `${label} is required.`;
      }
    }

    if (typeof value === "string") {
      if (minLength && value.length < minLength)
        return `${label} must be at least ${minLength} characters.`;
      if (maxLength && value.length > maxLength)
        return `${label} must be at most ${maxLength} characters.`;
    }

    if (type === "email" && value) {
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      if (!ok) return `Please enter a valid email address.`;
    }

    if (type === "tel" && value) {
      const ok = /^[0-9+()\-.\s]{6,}$/.test(value);
      if (!ok) return `Please enter a valid phone number.`;
    }

    if (
      type === "number" &&
      value !== "" &&
      value !== null &&
      value !== undefined
    ) {
      const n = Number(value);
      if (Number.isNaN(n)) return `${label} must be a number.`;
      if (min !== undefined && n < min) return `${label} must be ≥ ${min}.`;
      if (max !== undefined && n > max) return `${label} must be ≤ ${max}.`;
    }

    if (pattern && value) {
      try {
        const rx = new RegExp(pattern);
        if (!rx.test(value)) return `${label} is not in the correct format.`;
      } catch {
        // ignore bad patterns
      }
    }

    return null;
  };

  const validateAll = (vals) => {
    const out = {};
    for (const f of effectiveSchema) {
      out[f.id] = validateField(f, vals[f.id]);
    }
    return out;
  };

  useEffect(() => {
    const nextErrors = validateAll(formValues);
    setErrors(nextErrors);
    const allValid = Object.values(nextErrors).every((e) => !e);
    onValidityChange && onValidityChange(allValid);
    onChange && onChange(formValues);
    // eslint-disable-next-line
  }, [formValues]);

  // ---------- handlers ----------
  const clearLimitNotice = (id) => {
    setLimitNotices((previous) => {
      if (!previous[id]) return previous;
      const next = { ...previous };
      delete next[id];
      return next;
    });
  };

  const setLimitNotice = (id, message) => {
    setLimitNotices((previous) =>
      previous[id] === message ? previous : { ...previous, [id]: message }
    );
  };

  const updateTextValue = (id, field, incomingValue) => {
    const hardLimit = fieldHardLimit(field);
    const rawValue = stringValue(incomingValue);

    if (hardLimit && rawValue.length > hardLimit) {
      const overflow = rawValue.length - hardLimit;
      const safeValue = rawValue.slice(0, hardLimit);
      setFormValues((previous) => ({ ...previous, [id]: safeValue }));
      setLimitNotice(
        id,
        `${field.label || "This field"} accepts up to ${hardLimit.toLocaleString()} ` +
          `characters. ${overflow.toLocaleString()} extra character${
            overflow === 1 ? " was" : "s were"
          } not added.`
      );
      return;
    }

    clearLimitNotice(id);
    setFormValues((previous) => ({ ...previous, [id]: rawValue }));
  };

  const handleChange = (id, field, raw) => {
    if (field.type === "checkbox") {
      const value = !!raw.target.checked;
      setFormValues((p) => ({ ...p, [id]: value }));
      return;
    }
    if (field.type === "length") {
      // raw carries either { kind: 'mode', value } or { kind: 'number', value }
      const { kind, value } = raw;
      setFormValues((p) => {
        const curr = p[id] || { mode: "auto", value: null };
        if (kind === "mode") {
          // reset value when switching mode to avoid stale number
          return { ...p, [id]: { mode: value, value: null } };
        }
        if (kind === "number") {
          return { ...p, [id]: { ...curr, value } };
        }
        return p;
      });
      return;
    }

    const nextValue = raw?.target?.value ?? "";

    // During IME composition, preserve the browser's in-progress text. The
    // hard limit is applied once composition ends so international keyboards
    // remain natural and reliable.
    if (composingFieldsRef.current.has(id)) {
      setFormValues((previous) => ({
        ...previous,
        [id]: stringValue(nextValue),
      }));
      return;
    }

    updateTextValue(id, field, nextValue);
  };

  const handleCompositionStart = (id) => {
    composingFieldsRef.current.add(id);
  };

  const handleCompositionEnd = (id, field, event) => {
    composingFieldsRef.current.delete(id);
    updateTextValue(id, field, event?.currentTarget?.value ?? "");
  };

  const handlePaste = (id, field, event) => {
    const hardLimit = fieldHardLimit(field);
    if (!hardLimit) return;

    const pastedText = event.clipboardData?.getData("text");
    if (typeof pastedText !== "string") return;

    const target = event.currentTarget;
    const currentValue = stringValue(formValues[id]);
    const selectionStart = Number.isInteger(target.selectionStart)
      ? target.selectionStart
      : currentValue.length;
    const selectionEnd = Number.isInteger(target.selectionEnd)
      ? target.selectionEnd
      : selectionStart;
    const selectedLength = Math.max(0, selectionEnd - selectionStart);
    const projectedLength =
      currentValue.length - selectedLength + pastedText.length;

    if (projectedLength <= hardLimit) {
      clearLimitNotice(id);
      return;
    }

    event.preventDefault();

    const available = Math.max(
      0,
      hardLimit - (currentValue.length - selectedLength)
    );
    const acceptedText = pastedText.slice(0, available);
    const rejectedCount = pastedText.length - acceptedText.length;
    const nextValue =
      currentValue.slice(0, selectionStart) +
      acceptedText +
      currentValue.slice(selectionEnd);
    const nextCaret = selectionStart + acceptedText.length;

    setFormValues((previous) => ({ ...previous, [id]: nextValue }));
    setLimitNotice(
      id,
      `The field limit is ${hardLimit.toLocaleString()} characters. ` +
        `${rejectedCount.toLocaleString()} pasted character${
          rejectedCount === 1 ? " was" : "s were"
        } not added.`
    );

    window.requestAnimationFrame(() => {
      target.focus();
      target.setSelectionRange?.(nextCaret, nextCaret);
    });
  };

  const clampTextPosition = (value, position) => {
    const length = stringValue(value).length;
    const numeric = Number(position);
    if (!Number.isFinite(numeric)) return length;
    return Math.max(0, Math.min(length, Math.trunc(numeric)));
  };

  const rememberDetailsSelection = (element = detailsTextareaRef.current) => {
    const value = stringValue(
      element?.value !== undefined ? element.value : formValues.details
    );
    const fallback = value.length;
    const start = clampTextPosition(
      value,
      Number.isInteger(element?.selectionStart)
        ? element.selectionStart
        : detailsSelectionRef.current?.start
    );
    const end = clampTextPosition(
      value,
      Number.isInteger(element?.selectionEnd)
        ? element.selectionEnd
        : detailsSelectionRef.current?.end
    );

    detailsSelectionRef.current = {
      start: Number.isFinite(start) ? start : fallback,
      end: Number.isFinite(end) ? Math.max(start, end) : fallback,
    };

    return detailsSelectionRef.current;
  };

  const trimVoiceOverlapAgainstLeftContext = (left = "", transcript = "") => {
    const source = String(transcript || "").trim();
    if (!source) return "";

    const maxCheck = Math.min(50, left.length, source.length);
    for (let k = maxCheck; k > 0; k -= 1) {
      if (
        left.slice(-k).toLowerCase() === source.slice(0, k).toLowerCase()
      ) {
        return source.slice(k);
      }
    }

    return source;
  };

  const prepareVoiceInsertion = (left = "", right = "", transcript = "") => {
    let insertion = trimVoiceOverlapAgainstLeftContext(left, transcript);
    if (!insertion) return "";

    const leftChar = left.slice(-1);
    const rightChar = right.slice(0, 1);
    const firstInserted = insertion.slice(0, 1);
    const lastInserted = insertion.slice(-1);

    // Preserve all existing whitespace/newlines exactly. Add only the minimal
    // word-boundary spacing needed when the caret sits directly beside text.
    if (
      leftChar &&
      !/\s/.test(leftChar) &&
      firstInserted &&
      !/^[,.;:!?)}\]]$/.test(firstInserted)
    ) {
      insertion = ` ${insertion}`;
    }

    if (
      rightChar &&
      !/\s/.test(rightChar) &&
      lastInserted &&
      !/[\s([{<]$/.test(lastInserted) &&
      !/^[,.;:!?)}\]]$/.test(rightChar)
    ) {
      insertion = `${insertion} `;
    }

    return insertion;
  };

  const restoreDetailsCaretSoon = () => {
    const restore = () => {
      const element = detailsTextareaRef.current;
      const pending = pendingDetailsCaretRef.current;
      if (!element || !Number.isFinite(pending)) return false;

      const caret = clampTextPosition(element.value || "", pending);
      try {
        element.focus({ preventScroll: true });
      } catch {
        element.focus?.();
      }
      element.setSelectionRange?.(caret, caret);
      detailsSelectionRef.current = { start: caret, end: caret };
      pendingDetailsCaretRef.current = null;
      return true;
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!restore()) {
          window.setTimeout(restore, 0);
        }
      });
    });
  };

  const safeVoiceAction = (fn) => {
    const now = Date.now();
    if (now - (lastVoiceActionAtRef.current || 0) < 250) return;
    lastVoiceActionAtRef.current = now;
    fn?.();
  };

  const closeVoiceNotice = () => setVoiceNotice(null);

  const showVoiceNotice = (
    message,
    { title = "Voice input", kind = "info", dedupeKey = "" } = {}
  ) => {
    const safeMessage = String(message || "").trim();
    if (!safeMessage) return false;

    const key = String(dedupeKey || safeMessage);
    const now = Date.now();
    if (
      lastVoiceNoticeRef.current.key === key &&
      now - Number(lastVoiceNoticeRef.current.at || 0) < 900
    ) {
      return false;
    }

    lastVoiceNoticeRef.current = { key, at: now };
    setVoiceNotice({
      title: String(title || "Voice input"),
      message: safeMessage,
      kind: String(kind || "info"),
    });
    return true;
  };

  const notifyVoiceBlocked = (reason) => {
    // A host page may provide its own in-app billing modal. Returning false from
    // that callback explicitly asks this component to use its local modal instead.
    if (typeof onVoiceBlocked === "function") {
      try {
        const handled = onVoiceBlocked(reason);
        if (handled !== false) return;
      } catch {}
    }

    if (reason === "login_required") {
      showVoiceNotice("Please log in to use voice input.", {
        kind: "warning",
        dedupeKey: "voice-login-required",
      });
      return;
    }

    if (reason === "billing_loading") {
      showVoiceNotice(
        "Checking your Doc Writer access. Please try the microphone again in a moment.",
        {
          kind: "info",
          dedupeKey: "voice-billing-loading",
        }
      );
      return;
    }

    showVoiceNotice(
      "Voice input requires an available Doc Writer document or eligible plan. Please update your billing and try again.",
      {
        kind: "warning",
        dedupeKey: "voice-billing-required",
      }
    );
  };

  const voiceAccessIsReady = () => {
    if (!currentUser) {
      notifyVoiceBlocked("login_required");
      return false;
    }

    // Editor context represents an already-authorized D1/R1/R2 lifecycle.
    // Do not re-check current new-document plan/quota from this form.
    if (voiceUsesExistingDocumentEntitlement) {
      return true;
    }

    // Start context remains entitlement-aware before a new D1 is launched.
    if (voiceAccessLoading) {
      notifyVoiceBlocked("billing_loading");
      return false;
    }

    if (!voiceAccessAllowed) {
      notifyVoiceBlocked("billing_required");
      return false;
    }

    return true;
  };

  const pickBestMimeType = () => {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/mpeg",
    ];

    for (const type of candidates) {
      try {
        if (
          typeof window !== "undefined" &&
          window.MediaRecorder &&
          MediaRecorder.isTypeSupported(type)
        ) {
          return type;
        }
      } catch {}
    }

    return "";
  };

  const stopTracks = (stream) => {
    try {
      stream?.getTracks?.().forEach((track) => {
        try {
          track.stop();
        } catch {}
      });
    } catch {}
  };

  const resetVoiceState = () => {
    setRecording(false);
    setVoiceReady(false);
    setVoiceText("");
    setWaveformData([]);
    isFlatRef.current = true;

    recordedChunksRef.current = [];
    recordedBlobRef.current = null;
    recordedMimeRef.current = "";
    pendingAcceptRef.current = false;
    stopPromiseRef.current = null;
    recordStartAtRef.current = 0;

    setIsAcceptingVoice(false);
  };

  const ensureRecorderStopped = () => {
    try {
      const recorder = mediaRecorderRef.current;
      if (!recorder) return Promise.resolve();
      if (recorder.state === "inactive") return Promise.resolve();
      if (stopPromiseRef.current) return stopPromiseRef.current;

      stopPromiseRef.current = new Promise((resolve) => {
        const previousOnStop = recorder.onstop;
        recorder.onstop = (event) => {
          try {
            previousOnStop?.(event);
          } catch {}
          resolve();
          stopPromiseRef.current = null;
        };

        try {
          recorder.requestData?.();
        } catch {}

        try {
          recorder.stop();
        } catch {
          resolve();
          stopPromiseRef.current = null;
        }
      });

      return stopPromiseRef.current;
    } catch {
      return Promise.resolve();
    }
  };

  const insertVoiceTextAtDetailsCaret = (transcript) => {
    const detailsField = effectiveSchema.find((field) => field.id === "details");
    if (!detailsField) return;

    setFormValues((previous) => {
      const existing = stringValue(previous.details);
      const saved = detailsSelectionRef.current || {};
      const fallback = existing.length;

      const start = clampTextPosition(
        existing,
        Number.isFinite(saved.start) ? saved.start : fallback
      );
      const end = clampTextPosition(
        existing,
        Number.isFinite(saved.end) ? saved.end : start
      );
      const selectionStart = Math.min(start, end);
      const selectionEnd = Math.max(start, end);

      const left = existing.slice(0, selectionStart);
      const right = existing.slice(selectionEnd);
      const prepared = prepareVoiceInsertion(left, right, transcript);

      const hardLimit = fieldHardLimit(detailsField);
      const preservedLength =
        existing.length - Math.max(0, selectionEnd - selectionStart);
      const available = hardLimit
        ? Math.max(0, hardLimit - preservedLength)
        : prepared.length;
      const accepted = hardLimit ? prepared.slice(0, available) : prepared;
      const rejectedCount = Math.max(0, prepared.length - accepted.length);

      const nextValue = left + accepted + right;
      const nextCaret = left.length + accepted.length;

      detailsSelectionRef.current = {
        start: nextCaret,
        end: nextCaret,
      };
      pendingDetailsCaretRef.current = nextCaret;

      if (rejectedCount > 0 && hardLimit) {
        window.setTimeout(() => {
          setLimitNotice(
            "details",
            `Key Facts / Details accepts up to ${hardLimit.toLocaleString()} characters. ` +
              `${rejectedCount.toLocaleString()} transcribed character${
                rejectedCount === 1 ? " was" : "s were"
              } not added.`
          );
        }, 0);
      } else {
        window.setTimeout(() => clearLimitNotice("details"), 0);
      }

      return { ...previous, details: nextValue };
    });
  };

  const getIdTokenSafe = async () => {
    try {
      if (!currentUser?.getIdToken) return null;
      return await currentUser.getIdToken();
    } catch (error) {
      console.warn("[DocDynamicFieldsForm] getIdToken failed:", error);
      return null;
    }
  };

  const transcribeAndAppend = async (blob, mimeType) => {
    // Re-check access immediately before the paid STT call.
    if (!voiceAccessIsReady()) {
      setIsAcceptingVoice(false);
      return;
    }

    if (!HOST) {
      showVoiceNotice("Backend URL not configured (REACT_APP_BACKEND_URL).", {
        kind: "error",
        dedupeKey: "voice-backend-url",
      });
      setIsAcceptingVoice(false);
      return;
    }

    if (!blob || blob.size <= 0) {
      showVoiceNotice("No audio captured. Try again.", {
        kind: "warning",
        dedupeKey: "voice-no-audio",
      });
      setIsAcceptingVoice(false);
      return;
    }

    if (blob.size < MIN_BLOB_BYTES) {
      showVoiceNotice(
        "Audio too quiet/short. Please speak a bit longer and try again.",
        {
          kind: "warning",
          dedupeKey: "voice-audio-too-short-transcribe",
        }
      );
      setIsAcceptingVoice(false);
      return;
    }

    const token = await getIdTokenSafe();
    if (!token) {
      notifyVoiceBlocked("login_required");
      setIsAcceptingVoice(false);
      return;
    }

    setIsTranscribing(true);
    setVoiceText("");

    try {
      const ext = (mimeType || "").includes("mp4")
        ? "m4a"
        : (mimeType || "").includes("mpeg")
        ? "mp3"
        : "webm";

      const file = new File([blob], `speech.${ext}`, {
        type: mimeType || "audio/webm",
      });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("language", "auto");
      formData.append("sessionId", sessionId || "");
      formData.append("sessionKind", voiceSessionKind || "fast_chat");
      formData.append("userEmail", email || currentUser?.email || "");
      formData.append("userType", userType || "");

      console.log("[STT][docwriter] uploading:", {
        url: `${HOST}/stt/transcribe`,
        size: blob.size,
        mime: mimeType || "audio/webm",
        filename: `speech.${ext}`,
      });

      const response = await fetch(`${HOST}/stt/transcribe`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.ok) {
        const reason =
          data?.reason || data?.error || `stt_failed_${response.status}`;
        throw new Error(reason);
      }

      const transcript = String(data?.text || data?.transcript || "").trim();
      if (!transcript) {
        throw new Error("empty_transcript");
      }

      setVoiceText(transcript);
      insertVoiceTextAtDetailsCaret(transcript);
      resetVoiceState();
      restoreDetailsCaretSoon();
    } catch (error) {
      console.error("[DocDynamicFieldsForm] Transcribe failed:", error);
      showVoiceNotice(
        `Voice transcription failed (${String(
          error?.message || error
        )}). Please try again.`,
        {
          kind: "error",
          dedupeKey: `voice-transcription-${String(error?.message || error)}`,
        }
      );
      setVoiceReady(true);
    } finally {
      setIsTranscribing(false);
      setIsAcceptingVoice(false);
    }
  };

  const startRecording = async () => {
    if (!voiceAccessIsReady()) return;
    if (isTranscribing || isAcceptingVoice || recording) return;

    if (!navigator?.mediaDevices?.getUserMedia) {
      showVoiceNotice("Microphone not supported on this device/browser.", {
        kind: "warning",
        dedupeKey: "voice-mic-unsupported",
      });
      return;
    }

    recordedChunksRef.current = [];
    recordedBlobRef.current = null;
    recordedMimeRef.current = "";
    setVoiceReady(false);
    setVoiceText("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const mimeType = pickBestMimeType();
      recordedMimeRef.current = mimeType || "";

      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event?.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstart = () => {
        recordStartAtRef.current = Date.now();
        setRecording(true);
        isFlatRef.current = false;
      };

      recorder.onstop = () => {
        setRecording(false);
        isFlatRef.current = true;

        const chunks = recordedChunksRef.current || [];
        const type =
          recordedMimeRef.current || chunks?.[0]?.type || "audio/webm";
        const blob = new Blob(chunks, { type });
        recordedBlobRef.current = blob;

        try {
          stopTracks(streamRef.current);
        } catch {}
        streamRef.current = null;

        const durationMs = recordStartAtRef.current
          ? Date.now() - recordStartAtRef.current
          : 0;

        console.log("[STT][docwriter] onstop:", {
          durationMs,
          size: blob?.size || 0,
          mime: type,
          chunks: chunks?.length || 0,
        });

        if (!blob || blob.size <= 0) {
          setVoiceReady(false);
          recordedBlobRef.current = null;
          recordedChunksRef.current = [];
          showVoiceNotice(
            "No audio captured. Please try again and speak a little longer.",
            {
              kind: "warning",
              dedupeKey: "voice-no-audio-longer",
            }
          );
          setIsAcceptingVoice(false);
          return;
        }

        if (durationMs > 0 && durationMs < MIN_RECORD_MS) {
          setVoiceReady(false);
          recordedBlobRef.current = null;
          recordedChunksRef.current = [];
          showVoiceNotice(
            "Recording too short. Please speak for at least 1 second and try again.",
            {
              kind: "warning",
              dedupeKey: "voice-recording-too-short",
            }
          );
          setIsAcceptingVoice(false);
          return;
        }

        if (blob.size < MIN_BLOB_BYTES) {
          setVoiceReady(false);
          recordedBlobRef.current = null;
          recordedChunksRef.current = [];
          showVoiceNotice(
        "Audio too quiet/short. Please speak a bit longer and try again.",
        {
          kind: "warning",
          dedupeKey: "voice-audio-too-short-stop",
        }
      );
          setIsAcceptingVoice(false);
          return;
        }

        setVoiceReady(true);

        if (pendingAcceptRef.current) {
          pendingAcceptRef.current = false;
          transcribeAndAppend(blob, type);
        }
      };

      recorder.start(250);
    } catch (error) {
      console.warn("[DocDynamicFieldsForm] Mic start error:", error);
      resetVoiceState();
      showVoiceNotice(
        "Microphone access is blocked or unavailable. Please allow microphone permission and try again.",
        {
          kind: "warning",
          dedupeKey: "voice-mic-permission",
        }
      );
    }
  };

  const stopRecording = async () => {
    try {
      try {
        mediaRecorderRef.current?.requestData?.();
      } catch {}
      await new Promise((resolve) => window.setTimeout(resolve, 180));
      await ensureRecorderStopped();
    } catch (error) {
      console.warn("[DocDynamicFieldsForm] stopRecording failed:", error);
    }
  };

  const cancelVoice = () => {
    safeVoiceAction(() => {
      if (isTranscribing) return;

      try {
        const recorder = mediaRecorderRef.current;
        if (recorder && recorder.state !== "inactive") {
          recorder.ondataavailable = null;
          recorder.onstop = null;
          recorder.stop();
        }
      } catch {}

      mediaRecorderRef.current = null;

      try {
        stopTracks(streamRef.current);
      } catch {}
      streamRef.current = null;

      setIsAcceptingVoice(false);
      const saved = detailsSelectionRef.current || {};
      pendingDetailsCaretRef.current = Number.isFinite(saved.start)
        ? saved.start
        : null;
      resetVoiceState();
      restoreDetailsCaretSoon();
    });
  };

  const handleVoiceInput = () => {
    safeVoiceAction(() => {
      rememberDetailsSelection();
      if (!voiceAccessIsReady()) return;
      if (recording || isTranscribing || isAcceptingVoice) return;

      if (voiceReady) {
        resetVoiceState();
      }

      startRecording();
    });
  };

  const acceptVoice = () => {
    safeVoiceAction(async () => {
      if (isTranscribing || isAcceptingVoice) return;
      if (!voiceAccessIsReady()) return;

      setIsAcceptingVoice(true);

      try {
        if (recording) {
          const durationMs = recordStartAtRef.current
            ? Date.now() - recordStartAtRef.current
            : 0;

          if (durationMs > 0 && durationMs < MIN_RECORD_MS) {
            showVoiceNotice(
              "Recording too short. Please speak for at least 1 second.",
              {
                kind: "warning",
                dedupeKey: "voice-recording-too-short-accept",
              }
            );
            setIsAcceptingVoice(false);
            return;
          }

          pendingAcceptRef.current = true;
          await stopRecording();
          return;
        }

        if (voiceReady && recordedBlobRef.current) {
          await transcribeAndAppend(
            recordedBlobRef.current,
            recordedMimeRef.current || recordedBlobRef.current.type
          );
          return;
        }

        setIsAcceptingVoice(false);
      } catch (error) {
        console.warn("[DocDynamicFieldsForm] acceptVoice failed:", error);
        setIsAcceptingVoice(false);
      }
    });
  };

  const showVoiceWave =
    recording || voiceReady || isTranscribing || isAcceptingVoice;

  const Spinner = ({ size = 16 }) => (
    <span
      aria-label="Loading"
      style={{
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        willChange: "transform",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)",
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        style={{
          display: "block",
          transformBox: "fill-box",
          transformOrigin: "50% 50%",
          WebkitTransformOrigin: "50% 50%",
          animation: "asklawlyDocWriterSpin360 0.85s linear infinite",
          WebkitAnimation: "asklawlyDocWriterSpin360 0.85s linear infinite",
        }}
      >
        <circle
          cx="12"
          cy="12"
          r="9"
          fill="none"
          stroke="rgba(0,0,0,0.18)"
          strokeWidth="3"
        />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          fill="none"
          stroke="#111827"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );

  const markTouched = (id) =>
    setTouched((p) => (p[id] ? p : { ...p, [id]: true }));

  // ---------- styles ----------
  const baseInput = {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 14,
    background: "#fff",
  };

  const s = {
    wrap: {
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: 16,
      background: "#fff",
      display: "flex",
      flexWrap: "wrap",
      gap: 16,
      alignItems: "flex-start",
    },
    // responsive column; wraps on small screens
    column: { flex: "1 1 320px", minWidth: 260 },
    // force an element to take the entire row width
    fullRow: { flexBasis: "100%" },
    // a compact column (e.g., for the small checkbox)
    compactCol: { flex: "0 0 220px", minWidth: 200 },

    label: { display: "block", fontWeight: 600, fontSize: 13, marginBottom: 6 },
    required: { color: "#dc2626" },
    help: { fontSize: 12, color: "#6b7280", marginTop: 4 },

    input: { ...baseInput },
    textarea: {
      ...baseInput,
      minHeight: 180,
      resize: "vertical",
      lineHeight: 1.55,
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
    },
    select: { ...baseInput },

    checkboxRow: { display: "flex", alignItems: "center", gap: 8 },
    error: { color: "#b91c1c", fontSize: 12, marginTop: 6 },
    characterMetaRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 12,
      marginTop: 7,
      fontSize: 12,
      lineHeight: 1.4,
    },
    characterGuidance: { color: "#64748b", flex: "1 1 auto" },
    characterWarning: { color: "#92400e", flex: "1 1 auto" },
    characterLimit: { color: "#b91c1c", flex: "1 1 auto" },
    characterCounter: {
      color: "#64748b",
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      fontWeight: 700,
    },
    characterCounterWarning: { color: "#92400e" },
    characterCounterLimit: { color: "#b91c1c" },
    limitNotice: {
      color: "#b91c1c",
      fontSize: 12,
      lineHeight: 1.4,
      marginTop: 6,
      fontWeight: 600,
    },

    // small inline row for the length picker’s number field (keeps UI tidy)
    inlineRow: { display: "flex", gap: 8, alignItems: "stretch" },
    inlineNumber: { ...baseInput, flex: "0 0 140px" },

    voiceShell: {
      width: "100%",
      position: "relative",
      borderRadius: 12,
      background: "#fff",
      overflow: "visible",
    },
    voiceTextarea: {
      ...baseInput,
      minHeight: 180,
      resize: "vertical",
      lineHeight: 1.55,
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
      border: "1px solid #d1d5db",
      borderRadius: 12,
      outline: "none",
      display: "block",
    },
    voiceWave: {
      height: 180,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      padding: "16px 12px 58px",
      border: "1px solid #d1d5db",
      borderRadius: 12,
      background: "#fff",
    },
    voiceBars: {
      display: "flex",
      width: "100%",
      gap: 2,
      alignItems: "center",
    },
    voiceActions: {
      position: "absolute",
      right: 16,
      bottom: 14,
      zIndex: 3,
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 8,
    },
    voiceButton: {
      backgroundColor: "#f2f2f2",
      border: "none",
      borderRadius: 8,
      padding: 10,
      minWidth: 38,
      minHeight: 38,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: "#111827",
      boxShadow: "0 2px 8px rgba(15,23,42,0.08)",
    },
    voiceHint: {
      marginTop: 7,
      fontSize: 12,
      lineHeight: 1.4,
      color: "#6B7280",
    },

    voiceNoticeBackdrop: {
      position: "fixed",
      inset: 0,
      zIndex: 100000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
      background: "rgba(15, 23, 42, 0.38)",
    },
    voiceNoticeCard: {
      width: "min(440px, calc(100vw - 32px))",
      maxWidth: "100%",
      borderRadius: 14,
      background: "#FFFFFF",
      border: "1px solid #E2E8F0",
      boxShadow: "0 24px 64px rgba(15, 23, 42, 0.24)",
      overflow: "hidden",
    },
    voiceNoticeHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "16px 18px 10px",
    },
    voiceNoticeTitle: {
      margin: 0,
      color: "#0F172A",
      fontSize: 16,
      lineHeight: 1.3,
      fontWeight: 800,
    },
    voiceNoticeClose: {
      width: 34,
      height: 34,
      flex: "0 0 34px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      border: 0,
      borderRadius: 8,
      background: "transparent",
      color: "#64748B",
      cursor: "pointer",
    },
    voiceNoticeBody: {
      padding: "2px 18px 18px",
      color: "#334155",
      fontSize: 14,
      lineHeight: 1.55,
      whiteSpace: "pre-wrap",
    },
    voiceNoticeFooter: {
      display: "flex",
      justifyContent: "flex-end",
      padding: "0 18px 18px",
    },
    voiceNoticeOkay: {
      minWidth: 84,
      minHeight: 38,
      padding: "8px 16px",
      border: 0,
      borderRadius: 9,
      background: "#1E2A78",
      color: "#FFFFFF",
      fontSize: 14,
      fontWeight: 700,
      cursor: "pointer",
    },
  };

  // ---------- render ----------
  return (
    <>
      <div style={s.wrap}>
        {effectiveSchema.map((field) => {
        const val = formValues[field.id];
        const err = errors[field.id];
        const showErr = touched[field.id] && !!err;

        const isUrgent = field.id === "urgent";
        const isDetails = field.id === "details";
        const textVal = stringValue(val);
        const hardLimit = fieldHardLimit(field);
        const softLimit = fieldSoftLimit(field, hardLimit);
        const characterCount = textVal.length;
        const atHardLimit = Boolean(
          hardLimit && characterCount >= hardLimit
        );
        const atSoftWarning = Boolean(
          softLimit && characterCount >= softLimit
        );
        const limitNotice = limitNotices[field.id] || "";
        const helpId = `f-${field.id}-help`;
        const errorId = `f-${field.id}-error`;
        const counterId = `f-${field.id}-counter`;
        const noticeId = `f-${field.id}-limit-notice`;
        const describedBy = [
          field.help ? helpId : null,
          field.type === "textarea" && hardLimit ? counterId : null,
          limitNotice ? noticeId : null,
          showErr ? errorId : null,
        ]
          .filter(Boolean)
          .join(" ") || undefined;

        // Layout goals:
        // 1) "urgent" stays LEFT (small/compact).
        // 2) "details" spans FULL WIDTH to expand right.
        // 3) Everything else uses the normal responsive column.
        const colStyle = isDetails
          ? { ...s.column, ...s.fullRow, order: 1 }
          : isUrgent
          ? { ...s.compactCol, order: 0 }
          : s.column;

        return (
          <div key={field.id} style={colStyle}>
            {field.type !== "checkbox" ? (
              <label style={s.label} htmlFor={`f-${field.id}`}>
                {field.label}
                {field.required && <span style={s.required}>*</span>}
              </label>
            ) : (
              <label style={s.checkboxRow}>
                <input
                  id={`f-${field.id}`}
                  type="checkbox"
                  checked={!!val}
                  onChange={(e) => handleChange(field.id, field, e)}
                  onBlur={() => markTouched(field.id)}
                />
                <span style={{ fontWeight: 600 }}>{field.label}</span>
                {field.required && <span style={s.required}>*</span>}
              </label>
            )}

            {field.type === "text" && (
              <input
                id={`f-${field.id}`}
                type="text"
                value={textVal}
                placeholder={field.placeholder || ""}
                maxLength={hardLimit || undefined}
                aria-describedby={describedBy}
                aria-invalid={showErr || undefined}
                onChange={(e) => handleChange(field.id, field, e)}
                onCompositionStart={() => handleCompositionStart(field.id)}
                onCompositionEnd={(e) =>
                  handleCompositionEnd(field.id, field, e)
                }
                onBlur={() => markTouched(field.id)}
                style={s.input}
              />
            )}

            {field.type === "email" && (
              <input
                id={`f-${field.id}`}
                type="email"
                value={textVal}
                placeholder={field.placeholder || "name@example.com"}
                maxLength={hardLimit || undefined}
                aria-describedby={describedBy}
                aria-invalid={showErr || undefined}
                onChange={(e) => handleChange(field.id, field, e)}
                onCompositionStart={() => handleCompositionStart(field.id)}
                onCompositionEnd={(e) =>
                  handleCompositionEnd(field.id, field, e)
                }
                onBlur={() => markTouched(field.id)}
                style={s.input}
              />
            )}

            {field.type === "tel" && (
              <input
                id={`f-${field.id}`}
                type="tel"
                value={textVal}
                placeholder={field.placeholder || "+1 555 123 4567"}
                maxLength={hardLimit || undefined}
                aria-describedby={describedBy}
                aria-invalid={showErr || undefined}
                onChange={(e) => handleChange(field.id, field, e)}
                onCompositionStart={() => handleCompositionStart(field.id)}
                onCompositionEnd={(e) =>
                  handleCompositionEnd(field.id, field, e)
                }
                onBlur={() => markTouched(field.id)}
                style={s.input}
              />
            )}

            {field.type === "number" && (
              <input
                id={`f-${field.id}`}
                type="number"
                value={val}
                placeholder={field.placeholder || ""}
                min={field.min !== undefined ? field.min : undefined}
                max={field.max !== undefined ? field.max : undefined}
                onChange={(e) => handleChange(field.id, field, e)}
                onBlur={() => markTouched(field.id)}
                style={s.input}
              />
            )}

            {field.type === "date" && (
              <input
                id={`f-${field.id}`}
                type="date"
                value={val}
                onChange={(e) => handleChange(field.id, field, e)}
                onBlur={() => markTouched(field.id)}
                style={s.input}
              />
            )}

            {field.type === "textarea" && (
              <>
                {isDetails ? (
                  <>
                    <div style={s.voiceShell}>
                      {showVoiceWave ? (
                        <div style={s.voiceWave}>
                          <div style={s.voiceBars}>
                            {waveformData.map((value, index) => (
                              <div
                                key={index}
                                style={{
                                  flex: 1,
                                  height: `${value * 2}px`,
                                  backgroundColor: "#555",
                                  marginTop: `${10 - value / 2}px`,
                                  marginBottom: `${10 - value / 2}px`,
                                  borderRadius: 1,
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <textarea
                          ref={detailsTextareaRef}
                          className="doc-writer-details-voice-textarea"
                          id={`f-${field.id}`}
                          value={textVal}
                          placeholder={field.placeholder || ""}
                          maxLength={hardLimit || undefined}
                          aria-describedby={describedBy}
                          aria-invalid={showErr || undefined}
                          spellCheck={field.spellCheck !== false}
                          autoCapitalize={field.autoCapitalize || "sentences"}
                          autoCorrect={field.autoCorrect || "on"}
                          wrap="soft"
                          onChange={(e) => {
                            handleChange(field.id, field, e);
                            rememberDetailsSelection(e.currentTarget);
                          }}
                          onPaste={(e) => handlePaste(field.id, field, e)}
                          onSelect={(e) =>
                            rememberDetailsSelection(e.currentTarget)
                          }
                          onClick={(e) =>
                            rememberDetailsSelection(e.currentTarget)
                          }
                          onKeyUp={(e) =>
                            rememberDetailsSelection(e.currentTarget)
                          }
                          onFocus={(e) =>
                            rememberDetailsSelection(e.currentTarget)
                          }
                          onCompositionStart={() =>
                            handleCompositionStart(field.id)
                          }
                          onCompositionEnd={(e) => {
                            handleCompositionEnd(field.id, field, e);
                            rememberDetailsSelection(e.currentTarget);
                          }}
                          onBlur={(e) => {
                            rememberDetailsSelection(e.currentTarget);
                            markTouched(field.id);
                          }}
                          style={s.voiceTextarea}
                        />
                      )}

                      <div style={s.voiceActions}>
                        {showVoiceWave ? (
                          <>
                            <button
                              onPointerDown={cancelVoice}
                              type="button"
                              style={{
                                ...s.voiceButton,
                                cursor:
                                  isTranscribing || isAcceptingVoice
                                    ? "not-allowed"
                                    : "pointer",
                                opacity:
                                  isTranscribing || isAcceptingVoice ? 0.55 : 1,
                              }}
                              title="Cancel voice"
                              aria-label="Cancel voice"
                              disabled={isTranscribing || isAcceptingVoice}
                            >
                              <FaTimes />
                            </button>

                            <button
                              onPointerDown={acceptVoice}
                              type="button"
                              style={{
                                ...s.voiceButton,
                                cursor:
                                  isTranscribing ||
                                  isAcceptingVoice ||
                                  (!recording && !voiceReady)
                                    ? "not-allowed"
                                    : "pointer",
                                opacity:
                                  isTranscribing ||
                                  isAcceptingVoice ||
                                  (!recording && !voiceReady)
                                    ? 0.55
                                    : 1,
                              }}
                              title={recording ? "Stop & transcribe" : "Transcribe"}
                              aria-label={
                                recording ? "Stop and transcribe" : "Transcribe"
                              }
                              disabled={
                                isTranscribing ||
                                isAcceptingVoice ||
                                (!recording && !voiceReady)
                              }
                            >
                              {isTranscribing || isAcceptingVoice ? (
                                <Spinner size={16} />
                              ) : (
                                <FaCheck />
                              )}
                            </button>
                          </>
                        ) : (
                          <button
                            onPointerDown={handleVoiceInput}
                            type="button"
                            style={{
                              ...s.voiceButton,
                              opacity: voiceAccessLoading ? 0.65 : 1,
                            }}
                            title="Speak"
                            aria-label="Speak into Key Facts / Details"
                            disabled={isTranscribing || isAcceptingVoice}
                          >
                            <FiMic />
                          </button>
                        )}
                      </div>
                    </div>

                    {(isTranscribing || isAcceptingVoice) && (
                      <div style={s.voiceHint}>Transcribing…</div>
                    )}

                    {!!voiceText && !showVoiceWave && (
                      <div style={s.voiceHint}>
                        <span style={{ opacity: 0.9 }}>Last voice:</span>{" "}
                        {voiceText}
                      </div>
                    )}
                  </>
                ) : (
                  <textarea
                    id={`f-${field.id}`}
                    value={textVal}
                    placeholder={field.placeholder || ""}
                    maxLength={hardLimit || undefined}
                    aria-describedby={describedBy}
                    aria-invalid={showErr || undefined}
                    spellCheck={field.spellCheck !== false}
                    autoCapitalize={field.autoCapitalize || "sentences"}
                    autoCorrect={field.autoCorrect || "on"}
                    wrap="soft"
                    onChange={(e) => handleChange(field.id, field, e)}
                    onPaste={(e) => handlePaste(field.id, field, e)}
                    onCompositionStart={() =>
                      handleCompositionStart(field.id)
                    }
                    onCompositionEnd={(e) =>
                      handleCompositionEnd(field.id, field, e)
                    }
                    onBlur={() => markTouched(field.id)}
                    style={s.textarea}
                  />
                )}

                {hardLimit ? (
                  <div id={counterId} style={s.characterMetaRow}>
                    <div
                      style={
                        atHardLimit
                          ? s.characterLimit
                          : atSoftWarning
                          ? s.characterWarning
                          : s.characterGuidance
                      }
                    >
                      {atHardLimit
                        ? "Maximum reached. Add longer material as a supporting file."
                        : atSoftWarning
                        ? `For best results, keep this field focused. ${Math.max(
                            0,
                            hardLimit - characterCount
                          ).toLocaleString()} characters remain.`
                        : isDetails
                        ? "Type or paste the important facts, dates, names, amounts, deadlines, and requested outcome."
                        : ""}
                    </div>
                    <div
                      style={{
                        ...s.characterCounter,
                        ...(atHardLimit
                          ? s.characterCounterLimit
                          : atSoftWarning
                          ? s.characterCounterWarning
                          : null),
                      }}
                      aria-label={`${characterCount.toLocaleString()} of ${hardLimit.toLocaleString()} characters used`}
                    >
                      {characterCount.toLocaleString()} / {hardLimit.toLocaleString()}
                    </div>
                  </div>
                ) : null}

                {limitNotice ? (
                  <div
                    id={noticeId}
                    style={s.limitNotice}
                    role="status"
                    aria-live="polite"
                  >
                    {limitNotice}
                  </div>
                ) : null}
              </>
            )}

            {field.type === "select" && (
              <select
                id={`f-${field.id}`}
                value={val}
                onChange={(e) => handleChange(field.id, field, e)}
                onBlur={() => markTouched(field.id)}
                style={s.select}
              >
                <option value="">-- Select --</option>
                {(field.options || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}

            {/* NEW: length preference field */}
            {field.type === "length" && (
              <div>
                {/* Mode selector */}
<select
  id={`f-${field.id}-mode`}
  value={(val && val.mode) || "auto"}
  onChange={(e) =>
    handleChange(field.id, field, {
      kind: "mode",
      value: e.target.value,
    })
  }
  onBlur={() => markTouched(field.id)}
  style={{ ...s.select, width: "auto", display: "inline-block" }}   
>
  <option value="auto">Auto (recommended)</option>
  <option value="pages">By pages</option>
  <option value="words">By words</option>
</select>

                {/* Conditional number input */}
                {val && (val.mode === "pages" || val.mode === "words") ? (
                  <div style={s.inlineRow}>
                    <input
                      id={`f-${field.id}-num`}
                      type="number"
                      value={val.value ?? ""}
                      placeholder={
                        val.mode === "pages"
                          ? field.pagesPlaceholder || "e.g., 2"
                          : field.wordsPlaceholder || "e.g., 900"
                      }
                      min={
                        val.mode === "pages"
                          ? 1
                          : field.minWords !== undefined
                          ? field.minWords
                          : 50
                      }
                      max={
                        val.mode === "pages"
                          ? field.maxPages !== undefined
                            ? field.maxPages
                            : undefined
                          : field.maxWords !== undefined
                          ? field.maxWords
                          : undefined
                      }
                      onChange={(e) =>
                        handleChange(field.id, field, {
                          kind: "number",
                          value: e.target.value,
                        })
                      }
                      onBlur={() => markTouched(field.id)}
                      style={s.inlineNumber}
                    />
                  </div>
                ) : null}
              </div>
            )}

            {field.help && (
              <div id={helpId} style={s.help}>
                {field.help}
              </div>
            )}
            {showErr && (
              <div id={errorId} style={s.error} role="alert">
                {err}
              </div>
            )}
          </div>
        );
        })}
      </div>

      {voiceNotice && typeof document !== "undefined"
        ? createPortal(
            <div
              style={s.voiceNoticeBackdrop}
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) closeVoiceNotice();
              }}
            >
              <div
                style={s.voiceNoticeCard}
                role="dialog"
                aria-modal="true"
                aria-labelledby="docwriter-voice-notice-title"
                aria-describedby="docwriter-voice-notice-message"
                data-voice-notice-kind={voiceNotice.kind || "info"}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div style={s.voiceNoticeHeader}>
                  <h3
                    id="docwriter-voice-notice-title"
                    style={s.voiceNoticeTitle}
                  >
                    {voiceNotice.title || "Voice input"}
                  </h3>
                  <button
                    type="button"
                    style={s.voiceNoticeClose}
                    onClick={closeVoiceNotice}
                    aria-label="Close voice message"
                    title="Close"
                  >
                    <FaTimes />
                  </button>
                </div>

                <div
                  id="docwriter-voice-notice-message"
                  style={s.voiceNoticeBody}
                >
                  {voiceNotice.message}
                </div>

                <div style={s.voiceNoticeFooter}>
                  <button
                    type="button"
                    style={s.voiceNoticeOkay}
                    onClick={closeVoiceNotice}
                    autoFocus
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default DocDynamicFieldsForm;
